import { useEffect, useState } from 'react';
import BrandLogo from './BrandLogo';
import PosPendingApplications from './PosPendingApplications';
import PosSubscriptionStatusPill from './PosSubscriptionStatusPill';
import PosClientAccessControl from './PosClientAccessControl';
import { subscribePosClients } from '../utils/posClientUtil';
import { formatPlanDateInZone, getSubscriptionState } from '../utils/subscriptionDateUtil';
import { spinnerRing } from '../utils/bookingUtils';
import '../css/PosAdmin.css';

/**
 * Every registered client, as cards.
 *
 * The card is the client's front door: its address is the thing an
 * administrator most often needs from this screen — to hand to a new member of
 * staff, or to open the till and see what they are seeing.
 *
 * @param {object} props
 * @param {(client: object) => void} props.onEdit - open this client in the
 *   registration form.
 * @param {() => void} props.onAdd - jump to registration with a blank form.
 * @param {string} props.signedInEmail - the administrator, recorded as the
 *   reviewer of any application approved from the side panel.
 * @returns {JSX.Element}
 */
function PosClientList({ onEdit, onAdd, signedInEmail }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  // Distinct from an empty list on purpose: "nobody has registered a client"
  // and "the database refused to tell me" need completely different screens.
  const [loadError, setLoadError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setLoading(true);
    setLoadError(null);
    const stop = subscribePosClients(
      (list) => {
        setClients(list);
        setLoading(false);
      },
      (error) => {
        setLoadError(error);
        setLoading(false);
      },
    );
    return () => stop();
  }, [attempt]);

  if (loading) {
    return <div className="pa-loading">{spinnerRing('Loading clients')}</div>;
  }

  // A refused read is almost always the database rules: either they have not
  // been deployed (`firebase deploy --only database` — `npm run deploy` is
  // hosting only), or this account is not one the rules accept as an
  // administrator. Said plainly here rather than left as a silent empty list.
  if (loadError) {
    return (
      <div className="pa-screen">
        <header className="pa-screen-head">
          <div>
            <h1 className="pa-screen-title">Clients</h1>
            <p className="pa-screen-sub">The client list could not be read.</p>
          </div>
        </header>

        <div className="pa-empty">
          <h2 className="pa-empty-title">The database refused this read</h2>
          <p className="pa-empty-body">
            {loadError.message}
          </p>
          <p className="pa-empty-body">
            This is usually the rules rather than the data: check that
            <code className="pa-code"> database.rules.json </code> has been deployed with
            <code className="pa-code"> firebase deploy --only database </code>
            (<code className="pa-code">npm run deploy</code> publishes hosting only), and that you
            are signed in as an account the rules name as an administrator.
          </p>
          <button type="button" className="pa-btn pa-btn-primary" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  // The client cards on the left and the applications waiting for review on
  // the right: reviewing one usually means checking that client's card, so the
  // two share a screen rather than living on separate ones.
  return (
    <div className="ps-with-panel">
      <div className="pa-screen">
        <header className="pa-screen-head">
          <div>
            <h1 className="pa-screen-title">Clients</h1>
            <p className="pa-screen-sub">
              {clients.length === 0
                ? 'No clients registered yet.'
                : `${clients.length} client${clients.length === 1 ? '' : 's'} on this system.`}
            </p>
          </div>
          <button type="button" className="pa-btn pa-btn-primary" onClick={onAdd}>Register a client</button>
        </header>

        {clients.length === 0 ? (
          <div className="pa-empty">
            <h2 className="pa-empty-title">Start with one client</h2>
            <p className="pa-empty-body">
              Registering a client creates its till at <code className="pa-code">/&lt;client-id&gt;</code>,
              with its own products, stock, takings and audit trail.
            </p>
            <button type="button" className="pa-btn pa-btn-primary" onClick={onAdd}>Register a client</button>
          </div>
        ) : (
          <div className="pa-cards">
            {clients.map((client) => (
              <article className="pa-card" key={client.id}>
                <div className="pa-card-head">
                  <BrandLogo src={client.logoUrl} alt={client.name || client.id} className="pa-card-logo" />
                  <div className="pa-card-ident">
                    <h2 className="pa-card-name">{client.name || client.id}</h2>
                    <code className="pa-code">/{client.id}</code>
                  </div>
                </div>

                <dl className="pa-card-meta">
                  {client.email && (
                    <div><dt>Email</dt><dd>{client.email}</dd></div>
                  )}
                  {client.contact && (
                    <div><dt>Contact</dt><dd>{client.contact}</dd></div>
                  )}
                  <div>
                    <dt>Staff</dt>
                    <dd>
                      {client.staffCount === 0
                        ? 'None yet — nobody can sign in'
                        : `${client.staffCount} account${client.staffCount === 1 ? '' : 's'}`}
                    </dd>
                  </div>
                  <div>
                    <dt>Plan</dt>
                    <dd className="ps-card-plan">
                      {client.subscription ? (
                        <>
                          <span>{client.subscription.planName} · until {formatPlanDateInZone(client.subscription.endDate, client.timezone)}</span>
                          <PosSubscriptionStatusPill {...getSubscriptionState(client.subscription, client.timezone)} />
                          {client.access?.disabled && <span className="ps-pill ps-pill-bad">Till disabled</span>}
                        </>
                      ) : 'No subscription'}
                    </dd>
                  </div>
                </dl>

                <div className="pa-card-actions">
                  <button type="button" className="pa-btn pa-btn-quiet" onClick={() => onEdit(client)}>Edit</button>
                  {/* A plain link, not a router navigation: the till is a
                      different app surface, and opening it in its own tab means
                      an administrator checking a client does not lose their
                      place here. */}
                  <a className="pa-btn pa-btn-quiet" href={`/${client.id}`} target="_blank" rel="noreferrer">
                    Open POS
                  </a>
                </div>

                {/* Only on expired or already-disabled clients. */}
                <PosClientAccessControl client={client} signedInEmail={signedInEmail} />
              </article>
            ))}
          </div>
        )}
      </div>
      <PosPendingApplications signedInEmail={signedInEmail} />
    </div>
  );
}

export default PosClientList;
